import type { CSSProperties } from 'react'

const RING_PADDING = 8
// Matches the ring's rounded-lg, so the mask's corners follow the ring.
const HOLE_RADIUS = 8
// The top-left corner piece: a square with a quarter circle taken out of its inner corner.
const CORNER_CLIP_PATH = `path('M0 0 L${HOLE_RADIUS} 0 A${HOLE_RADIUS} ${HOLE_RADIUS} 0 0 0 0 ${HOLE_RADIUS} Z')`

export const tourTargetSelector = (target: string) => `[data-tour="${target}"]`

const hasSize = (box: DOMRect) => box.width > 0 || box.height > 0

// The first copy of the area that takes up space; a hidden copy has an empty box.
export const measureTourTarget = (target: string): DOMRect | null => {
  const elements = document.querySelectorAll(tourTargetSelector(target))
  for (const element of elements) {
    const box = element.getBoundingClientRect()
    if (hasSize(box)) return box
  }
  return null
}

export const isSameBox = (first: DOMRect | null, second: DOMRect | null) =>
  first?.x === second?.x &&
  first?.y === second?.y &&
  first?.width === second?.width &&
  first?.height === second?.height

export const holeAround = (box: DOMRect) =>
  new DOMRect(
    box.x - RING_PADDING,
    box.y - RING_PADDING,
    box.width + RING_PADDING * 2,
    box.height + RING_PADDING * 2,
  )

export const panelsAround = (hole: DOMRect): Record<string, CSSProperties> => ({
  above: { top: 0, left: 0, right: 0, height: Math.max(hole.top, 0) },
  below: { top: hole.bottom, left: 0, right: 0, bottom: 0 },
  left: {
    top: hole.top,
    height: hole.height,
    left: 0,
    width: Math.max(hole.left, 0),
  },
  right: { top: hole.top, height: hole.height, left: hole.right, right: 0 },
})

const cornerPiece = (
  top: number,
  left: number,
  quarterTurns: number,
): CSSProperties => ({
  top,
  left,
  width: HOLE_RADIUS,
  height: HOLE_RADIUS,
  clipPath: CORNER_CLIP_PATH,
  transform: `rotate(${quarterTurns * 90}deg)`,
})

// Fill the hole's square corners outside the ring's rounded ones, so the mask is rounded too.
export const cornersAround = (
  hole: DOMRect,
): Record<string, CSSProperties> => ({
  topLeft: cornerPiece(hole.top, hole.left, 0),
  topRight: cornerPiece(hole.top, hole.right - HOLE_RADIUS, 1),
  bottomRight: cornerPiece(
    hole.bottom - HOLE_RADIUS,
    hole.right - HOLE_RADIUS,
    2,
  ),
  bottomLeft: cornerPiece(hole.bottom - HOLE_RADIUS, hole.left, 3),
})
